<script setup>
import _ from "lodash"
import { computed, ref, onMounted, useTemplateRef } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Spinner from "@/components/ui/Spinner.vue"
import PlaylistService from "@/services/Playlistservice"
import AddPlaylistModal from "@/components/modals/AddPlaylistModal.vue"
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import TabGroup from "@/components/Atoms/TabGroup.vue"
import TabItem from "@/components/Atoms/TabItem.vue"
import PlaylistHeader from "../../components/Playlist/PlaylistHeader.vue"
import PlaylistSettings from "../../components/Playlist/PlaylistSettings.vue"
import PlaylistEmbed from "../../components/Playlist/PlaylistEmbed.vue"
import PlaylistPlayback from "../../components/Playlist/PlaylistPlayback.vue"
import PlaylistTable from "../../components/Playlist/PlaylistTable.vue"
import PlaylistItems from "../../components/Playlist/PlaylistItems.vue"
import AddPlaylistContentModal from "../../components/modals/AddPlaylistContentModal.vue"
import IconBase from "../../components/icon/IconBase.vue"
import IconPlus from "../../components/icon/IconPlus.vue"
import FormButton from "../../components/Atoms/FormButton.vue"
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const router = useRouter()
const addPlaylistModalRef = useTemplateRef('addPlaylistModalRef')
const addPlaylistContentModalRef = useTemplateRef('addPlaylistContentModalRef')

//Refs
const activeTab = ref('playback')
const isVisible = ref(false)
const playlistSettings = ref({})
const hasPlaylist = ref(false)
const features = ref({
  autoPlay: {
    status: false,
  },
  embedAutoplay: {
    status: false,
  },
  responsive: {
    status: true,
  },
  loop: {
    status: false,
  },
  shuffle: {
    status: false,
  },
  autoPlayNext: {
    status: false,
  },
  socialSharingEnabled: {
    status: false,
  },
  shareFacebook: {
    status: true,
  },
  shareTwitter: {
    status: true,
  },
  shareLinkedIn: {
    status: false
  },
  shareReddit: {
    status: false
  },
  shareEmail: {
    status: false
  },
  shareWhatsApp: {
    status: false
  },
  countryBlock: {
    enabled: false,
    value: [],
    valueType: 'array'
  },
  countryWhitelists: {
    enabled: false,
    value: [],
    valueType: 'array'
  },
  domainWhitelists: {
    enabled: false,
    value: [],
    valueType: 'array'
  },
	embedPassword: {
		enabled: false,
		value: null,
		valueType: 'string'
	},
})


// Computed

const loadingStatus = computed(() => store.getters["Ui/loadingStatus"])
const playlist = computed(() => store.getters["Playlist/playlist"])
const currentPlaylist = computed(() => store.getters["Playlist/currentPlaylist"])

// Hooks
onMounted(async () => {
  if (!store.getters['Playlist/isLoaded']) {
    store.dispatch('Ui/setLoadingStatus', true)
  } else {
    store.dispatch('Ui/setLoadingStatus', false)
  }
  try {
    await store.dispatch('Playlist/getPlaylist')
    await store.dispatch('Streams/getStreams')
    if (playlist.value.length > 0) {
      hasPlaylist.value = true
      setupPlaylistSettings()
    } else {
      hasPlaylist.value = false
    }

    // Check if we have an invalid playlistId in the route
    const routePlaylistId = router.currentRoute.value.params.playlistId
    if (routePlaylistId && !currentPlaylist.value) {
      router.push({ path: '/dashboard' }).catch((error) => { console.log(error) })
    } else if ((playlist.value.length > 0) && !routePlaylistId) {
      router.push({ path: '/playlists/' + playlist.value[0]._id }).catch((error) => { console.log(error) })
    } else if (playlist.value.length === 0) {
      router.push({ path: '/playlists' }).catch((error) => { console.log(error) })
    }
  } finally {
    store.dispatch('Ui/setLoadingStatus', false)
  }
})

// Methods

const setTab = (tab) => {
  activeTab.value = tab
}

const isTabActive = (tab) => tab === activeTab.value

const createdPlaylist = (playlist) => {
  hasPlaylist.value = true
  router.push({ path: '/playlists/' + playlist._id }).catch(() => { })
}

const toPlaylistEntry = (selectedStream, type) => {
  const stream = { ...selectedStream }
  if (type !== 'livestream' && !stream.refId) {
    stream._id = stream.id
  }
  if (type !== 'livestream' && stream.refId) {
    stream._id = stream.refId
  }
  let streamId = ''
  let streamName = ''
  let streamType = ''
  let isLivetovod = false
  if (type === 'livestream') {
    streamId = stream._id
    streamName = stream.name
    streamType = 'live'
  } else {
    streamId = stream.id
    streamName = stream.fileName
    streamType = 'vod'
  }
  if (stream.refId) {
    streamId = stream.refId
    isLivetovod = true
  }
  return {
    id: streamId,
    type: streamType,
    isLiveToVod: isLivetovod,
    name: streamName,
  }
}

const isPlaylistStreamEntries = (payload) => {
  if (!Array.isArray(payload)) return false
  if (payload.length === 0) return true
  return payload.every(
    item => item && typeof item.id === 'string' && typeof item.type === 'string' && typeof item.name === 'string'
  )
}

const onChange = async (payload = null, type) => {
  if (isPlaylistStreamEntries(payload)) {
    await addToplaylist(_.cloneDeep(payload), true)
    return
  }

  if (!payload) return

  const streams = _.cloneDeep(currentPlaylist.value.streams)
  const entry = toPlaylistEntry(payload, type)
  const streamExists = streams.some(el => el.id === entry.id)
  if (!streamExists) {
    streams.unshift(entry)
    await addToplaylist(streams, true)
  } else {
    notify({ group: 'error', text: 'You already have this item in your playlist.' })
  }
}

const addToplaylist = async (streamArray, back = false) => {
  try {
    await PlaylistService.updatePlaylist(currentPlaylist.value.name, streamArray, currentPlaylist.value._id)
    const entries = Array.isArray(streamArray) ? streamArray : []
    track(EVENTS.PLAYLIST_CONTENT_ADDED, {
      playlistId: currentPlaylist.value._id,
      contentCount: entries.length,
      liveCount: entries.filter(e => e?.type === 'live').length,
      vodCount: entries.filter(e => e?.type === 'vod').length,
    })
  } catch (e) {
    notify({ group: 'error', text: 'Could not update the playlist. Please try again.' })
  }
  await store.dispatch('Playlist/getPlaylist', true)
}

const removePlaylists = async (index = null) => {

  if (currentPlaylist.value) {
    store.dispatch('Ui/setLoadingStatus', true)
    const playlistName = currentPlaylist.value.name
    await PlaylistService.deletePlaylist(currentPlaylist.value._id)
    await store.dispatch('Playlist/getPlaylist', true)
    notify({
      group: 'success',
      text: `Playlist "${playlistName}" is completely deleted from your account`,
    })

    if (playlist.value.length === 0) hasPlaylist.value = false
    router.push({ path: '/playlists' })

    store.dispatch('Ui/setLoadingStatus', false)

  }
}

const updateSettingsDebounced = async () => {
  const newSettings = { ...playlistSettings.value, ...features.value }
  let pid = currentPlaylist.value._id
  if (pid) await PlaylistService.updateSettings(pid, newSettings)
}

const normalizeAutoplaySettings = () => {
  const fromApi = features.value.autoPlay ?? features.value.embedAutoplay
  if (!fromApi) return
  const normalized = typeof fromApi.status !== 'undefined'
    ? { ...fromApi }
    : { status: false }
  features.value.autoPlay = normalized
  features.value.embedAutoplay = { ...normalized }
}

const setupPlaylistSettings = async (shouldReset = false) => {
  if (shouldReset) {
    await store.dispatch('Playlist/getPlaylist', true)
    playlistSettings.value = {}
  }
  if (currentPlaylist.value?.settings) {
    playlistSettings.value = currentPlaylist.value?.settings
    if (playlistSettings.value) {
      features.value = { ...features.value, ..._.cloneDeep(playlistSettings.value) }
      normalizeAutoplaySettings()
    } else {
      playlistSettings.value = {}
    }
  }
}

const onUpdateSecurity = async (payload) => {
  const { key, value } = payload
  features.value[key] = value
  if (key === 'autoPlay' || key === 'embedAutoplay') {
    features.value.autoPlay = value
    features.value.embedAutoplay = { ...value }
  }
  updateSettingsDebounced()
  await store.dispatch('Playlist/getPlaylist', true)
}

const showAddPlaylistModal = () => {
  addPlaylistModalRef.value?.toggleModal()
}

const showAddPlaylistContentModal = () => {
  isVisible.value = true
}

</script>

<template>
  <div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
    <Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <main v-else>
      <div class="flex items-stretch gap-4">
        <div class="grid grid-cols-12 gap-4 auto-rows-auto items-start w-full">
          <PlaylistHeader class="col-span-12" />
          <div class="col-span-12 xl:col-span-8 xl:block xl:h-full xl:flex xl:flex-col">
            <div class="flex items-center justify-between">
              <PlaylistItems :items="currentPlaylist?.streams" :show-total-items="false" />
              <form-button v-if="currentPlaylist?.streams?.length > 0" size="sm" @click="showAddPlaylistContentModal">
                <icon-base>
                  <icon-plus />
                </icon-base>
                Add content
              </form-button>
            </div>
            <PlaylistTable class=" col-span-12 flex-1" @add-item="showAddPlaylistContentModal" />
            <!-- <StreamsWidget placement="body" source="playlist" @add-stream="onChange" /> -->
          </div>
          <div class="col-span-12 xl:col-span-4">
            <TabGroup class="grid grid-cols-2 !mb-4">
              <TabItem centered @click="setTab('playback')" label="Playback" :isActive="isTabActive('playback')" />
              <TabItem centered @click="setTab('settings')" label="Settings" :isActive="isTabActive('settings')" />
            </TabGroup>
            <div class="">
              <section v-if="isTabActive('playback')">
                <PlaylistPlayback
                  :stream="currentPlaylist"
                  :settings="features"
                  @on-update="onUpdateSecurity"
                />
              </section>

              <section v-else-if="isTabActive('settings')">
                <PlaylistSettings :stream="currentPlaylist"
                  @on-update="onUpdateSecurity" />
              </section>
              <PlaylistEmbed
                :stream="currentPlaylist"
                :settings="features"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
    <add-playlist-modal ref="addPlaylistModalRef" @playlist-created="createdPlaylist" @add-stream="onChange" />
    <demo-video-modal videoid="tDgMUJ40LkS3jrnJeMTQc" />
    <confirm-modal modalId="delete-playlist" message="Are you sure you want to delete the playlist?" modal-type="danger"
      @modal-confirm="removePlaylists(currentIndexed)" />
    <add-playlist-content-modal v-model:is-visible="isVisible" ref="addPlaylistContentModalRef" @add-stream="onChange" />
  </div>
</template>

<style scoped></style>